import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';

const ProtectedRoute = ({ requireAdmin = false, children }) => {
  // Reutilizamos el estado de autenticación desde el AuthContext
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [userRole, setUserRole] = useState('user');

  // Rutas que siempre están permitidas (incluso con suscripción vencida)
  const alwaysAllowedRoutes = [
    '/pricing',
    '/my-account',
    '/subscription/success',
    '/subscription/cancel',
    '/package-success',
    '/boost-success',
    '/admin'  // Admin siempre permitido para administradores
  ];

  // Rutas permitidas para taxi_admin (sin ser admin)
  const taxiAdminAllowedRoutes = [
    '/taxi-dispatch',
    '/taxi-bot-config',
    '/rules',
    '/actions',
    '/whatsapp-setup',
    '/whatsapp-web',
    '/my-account',
    '/pricing',
  ];

  // Verificar rol de admin y estado de suscripción
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let isMounted = true;

    const checkPermissions = async () => {
      if (!currentUser) {
        if (isMounted) setSubscriptionLoading(false);
        return;
      }

      try {
        const token = await currentUser.getIdToken();

        // Verificar admin y suscripción en paralelo
        const [adminRes, subRes] = await Promise.all([
          fetch(`${API_URL}/admin/check-role`, {
            headers: { 'Authorization': `Bearer ${token}` },
            cache: 'no-store',
            signal
          }),
          fetch(`${API_URL}/subscription/status`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal
          })
        ]);

        // Procesar respuesta de admin
        if (adminRes.ok) {
          const adminData = await adminRes.json();
          if (isMounted) {
            console.log('[ProtectedRoute] 📊 Admin check:', adminData);
            setIsAdmin(!!adminData.isAdmin);
            setUserRole(adminData.role || 'user');
          }
        } else {
          if (isMounted) {
            console.log('[ProtectedRoute] ⚠️ Admin check falló, status:', adminRes.status);
            setIsAdmin(false);
          }
        }

        // Procesar respuesta de suscripción
        if (subRes.ok) {
          const subData = await subRes.json();
          if (isMounted) setSubscriptionStatus(subData.subscription?.status || 'unknown');
        } else {
          if (isMounted) setSubscriptionStatus('unknown');
        }

      } catch (err) {
        if (err.name !== 'AbortError') {
            console.error('Error verificando permisos:', err);
            if (isMounted) {
                setIsAdmin(false);
                setSubscriptionStatus('unknown');
            }
        }
      } finally {
        if (isMounted) {
            setAdminLoading(false);
            setSubscriptionLoading(false);
        }
      }
    };

    if (currentUser) {
      if (isMounted) {
        setAdminLoading(true);
        setSubscriptionLoading(true);
      }
      checkPermissions();
    }

    return () => {
        isMounted = false;
        controller.abort();
    };
  }, [currentUser]);

  // ⚠️ useMemo DEBE estar antes de cualquier return condicional (Rules of Hooks)
  const redirectState = React.useMemo(() => ({ from: location, expired: true }), [location]);

  // Mientras se verifica, no mostramos nada para evitar "parpadeos"
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Si no hay usuario, redirigimos a la página de login.
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Mientras carga permisos
  if (adminLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si se requiere admin específicamente, verificamos permisos
  if (requireAdmin) {
    console.log('[ProtectedRoute] 🛡️ requireAdmin=true, isAdmin=', isAdmin);
    if (!isAdmin) {
      console.log('[ProtectedRoute] ❌ Redirigiendo a /dashboard (no es admin)');
      return <Navigate to="/dashboard" />;
    }
  }

  // 🚕 Si es taxi_admin (y NO es admin), restringir a sus rutas permitidas
  if (userRole === 'taxi_admin' && !isAdmin) {
    const isAllowed = taxiAdminAllowedRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );
    if (!isAllowed) {
      console.log('[ProtectedRoute] 🚕 taxi_admin redirigido a /taxi-dispatch desde:', location.pathname);
      return <Navigate to="/taxi-dispatch" />;
    }
  }

  // Verificar si la ruta actual está siempre permitida
  const isRouteAlwaysAllowed = alwaysAllowedRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  console.log('[ProtectedRoute] 📍 Ruta:', location.pathname, '| isAdmin:', isAdmin, '| subscriptionStatus:', subscriptionStatus, '| isRouteAlwaysAllowed:', isRouteAlwaysAllowed);

  // Si la suscripción está vencida y NO es admin y NO es ruta permitida
  if (subscriptionStatus === 'expired' && !isAdmin && !isRouteAlwaysAllowed) {
    console.log('[ProtectedRoute] 🛑 Suscripción expirada, redirigiendo a /pricing');
    return <Navigate to="/pricing" state={redirectState} />;
  }

  // Si sí hay usuario (y permisos si aplica), mostramos el contenido protegido.
  // Prioridad: children (si se usa como wrapper) > Outlet (si se usa como layout route)
  // ✅ FIX: Usar Outlet normal aquí; el remount con key se hace en AppLayout
  return children ? children : <Outlet />;
};

export default ProtectedRoute;
